import {
  BadRequest,
  CartItemValidation,
  CartValidation,
  FatalError,
  ItemCategories,
  ItemInput,
  NotFound,
  Order,
  UpdateOrderErrors,
  UpdateOrderItemsInput,
} from '../../gql/graphql';
import { createForbiddenErrorMessage, createUnauthorizedErrorMessage } from '../../helpers/security/security';
import orderService from '../../services/order.service';
import { NetworkError } from '../../state-machine/actions/genericActions';
import { CartItemModel, nextId } from '../../state-machine/types/existingApplicationMachine.types';

export type UpdateOrderItemsResult = {
  order?: Order;
  updateOrderErrors: UpdateOrderErrors[]; // generic order errors (CV errors not included)
  cartValidationErrors: CartValidation[]; // cart level errors
  cartValidationItemErrors: Record<string, CartItemValidation> | undefined; // item level errors
  cartValidationItemWarnings: Record<string, CartItemValidation> | undefined; // item level warnings
};

export const OtherCharges = 'Other Charges';

const updateOrderItems = async (context: { orderId: string; cartTotal: number; cart: CartItemModel[] }): Promise<UpdateOrderItemsResult> => {
  try {
    const sumOfItems = context.cart.reduce((total, cartItem) => total + Math.round(cartItem.price * 100), 0) / 100;

    const itemsToUpdate = context.cart.map((cur: CartItemModel) => {
      const totalPriceInCents = Math.round(cur.price * 100);

      const unitPriceInCents = Math.round(totalPriceInCents / cur.quantity);
      const unitPrice = unitPriceInCents / 100;

      return {
        id: cur.id,
        displayName: cur.itemName,
        quantity: cur.quantity,
        price: unitPrice.toString(),
      } as ItemInput;
    });

    // add fees
    const otherCharges = {
      id: nextId(context.cart),
      displayName: OtherCharges,
      category: ItemCategories.Service,
      quantity: 1,
      price: context.cartTotal - sumOfItems,
    };

    const updateOrderItemsInput = {
      orderId: context.orderId,
      items: [...itemsToUpdate, otherCharges],
      shippingAmnt: '0',
      taxAmnt: '0',
    } as UpdateOrderItemsInput;

    const result = await orderService.updateOrderItems(updateOrderItemsInput);

    return {
      order: result?.data?.updateOrderItems.order,

      updateOrderErrors: result?.data?.updateOrderItems?.orderErrors?.filter(
        (e): e is BadRequest | FatalError | NotFound => e.__typename === 'BadRequest' || e.__typename === 'FatalError' || e.__typename === 'NotFound',
      ),

      cartValidationErrors: result?.data?.updateOrderItems.orderErrors?.filter((e): e is CartValidation => e.__typename === 'CartValidation'),

      cartValidationItemErrors: result?.data?.updateOrderItems?.orderErrors
        // take the cv error out of orderErrors,
        ?.filter((e): e is CartValidation => e.__typename === 'CartValidation')
        // find the first (only) cv error with a cartItemValidationBlock,
        ?.find((cv) => !!cv?.extensions?.cartItemValidationBlock?.length)
        //  map all of the item block errors into a map[id]cvItemBlock
        ?.extensions?.cartItemValidationBlock?.reduce((map: Record<string, CartItemValidation>, cvItemBlock: CartItemValidation | null) => {
          if (cvItemBlock?.id !== null && cvItemBlock?.id !== undefined) {
            map[cvItemBlock.id] = cvItemBlock;
          }
          return map;
        }, {}),

      cartValidationItemWarnings: result?.data?.updateOrderItems?.cartItemValidationWarn?.reduce(
        (acc: Record<string, CartItemValidation>, item: CartItemValidation | null) => {
          if (item?.id !== null && item?.id !== undefined) {
            acc[item.id] = item;
          }
          return acc;
        },
        {},
      ),
    } as UpdateOrderItemsResult;
  } catch (err: any) {
    const error = {} as NetworkError;
    switch (err?.networkError?.statusCode) {
      case 403:
        error.forbiddenError = createForbiddenErrorMessage(err);
        break;
      case 401:
        error.unauthorizedError = createUnauthorizedErrorMessage(err);
        break;
      default:
        error.errorGeneral = err;
    }
    throw error;
  }
};

export default updateOrderItems;
